// ============================================================================
// S16 Ravo Connect — Shopify integration Pinia store.
//
// Orchestrates the operator journey against the FROZEN Gate-2b control-plane:
//   connect (mocked OAuth) -> discover locations -> map (link/create/ignore)
//   -> configure scope + preview -> initial sync (durable phases) -> operate.
//
// Durable truth lives on the backend. This store only *caches* the last read
// of integration state / locations / runs so the UI can render; on refresh the
// view re-fetches by id and resumes the same run. NOTHING here is persisted to
// localStorage (no pinia-persist), and every payload from the service is
// already credential-stripped — we additionally never write a secret-shaped
// field ourselves.
// ============================================================================
import { defineStore } from "pinia";
import { ref } from "vue";
import { useToast } from "vue-toastification";
import shopifyService from "@/services/shopify";
import {
  canonicalShopDomain,
  stripCredentials,
  validateMappingChange,
  unresolvedActiveLocations,
  pendingActiveLocationRecords,
} from "@/utils/helpers/shopify";

export const useShopifyStore = defineStore("shopify", () => {
  const toast = useToast();

  // ---- reactive caches (NEVER credentials) --------------------------------
  const loading = ref(false); // generic in-flight for reads
  const connecting = ref(false); // install/authorizing in-flight
  const commandLoading = ref(""); // which operate-command is running ("sync" | "pause" | ...)
  const integrations = ref([]); // IntegrationResource[]
  const current = ref(null); // IntegrationResource
  const locations = ref([]); // ProviderLocationResource[]
  const runs = ref([]); // SyncRunResource[]
  const activeRun = ref(null); // SyncRunResource

  // Guard: refuse to commit anything credential-shaped into state.
  function commit(refTarget, value) {
    refTarget.value = stripCredentials(value);
    return refTarget.value;
  }

  function _err(err) {
    toast.error(err?.data?.message || err?.message);
    throw err;
  }

  // ---- Connect (mocked OAuth) ---------------------------------------------

  /**
   * Request a signed install URL for a canonical *.myshopify.com domain.
   * Returns { install_url, state }. The token itself is never returned here.
   * Rejects locally (without a network call) when the domain is not canonical.
   */
  async function requestInstallUrl(companyId, rawDomain, returnUrl) {
    const shop_domain = canonicalShopDomain(rawDomain);
    if (!shop_domain) {
      const e = { data: { message: "Enter a valid myshopify.com store domain." } };
      toast.error(e.data.message);
      throw e;
    }
    connecting.value = true;
    try {
      // S16 R12 DTO §1.5/§5 #1: the FROZEN wire field is `shop` (ai-back
      // ShopifyInstallRequest validates `shop`, not `shop_domain`). company_id is
      // authoritative from the route path, so it is NOT sent in the body.
      const res = await shopifyService.startInstall(companyId, {
        shop: shop_domain,
        ...(returnUrl ? { return_url: returnUrl } : {}),
      });
      return res; // { install_url, state } — no token
    } catch (err) {
      return _err(err);
    } finally {
      connecting.value = false;
    }
  }

  // S16 R12 (DTO §5 #3): there is NO FE-driven callback writer in production. The
  // browser is redirected to install_url and Shopify returns server-to-server to the
  // ai-back callback; the dashboard only re-reads the durable integration state on
  // return (fetchIntegration below). The old mocked completeConnect/completeCallback
  // pair was removed so the browser can never drive the OAuth callback.

  // ---- Read integrations / durable state ----------------------------------

  async function fetchIntegrations(companyId) {
    loading.value = true;
    try {
      const res = await shopifyService.list(companyId);
      commit(integrations, res.data || []);
      return integrations.value;
    } catch (err) {
      return _err(err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchIntegration(companyId, integrationId) {
    loading.value = true;
    try {
      const res = await shopifyService.get(companyId, integrationId);
      commit(current, res.data);
      return current.value;
    } catch (err) {
      return _err(err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchLocations(companyId, integrationId) {
    loading.value = true;
    try {
      const res = await shopifyService.getLocations(companyId, integrationId);
      commit(locations, res.data || []);
      return locations.value;
    } catch (err) {
      return _err(err);
    } finally {
      loading.value = false;
    }
  }

  async function discoverLocations(companyId, integrationId) {
    commandLoading.value = "discover";
    try {
      const res = await shopifyService.discoverLocations(companyId, integrationId, { company_id: companyId });
      if (res?.data?.id) commit(activeRun, res.data);
      return res;
    } catch (err) {
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  // ---- Mapping (link / create / ignore) -----------------------------------

  /**
   * Persist a batch of mapping changes. Each change is validated client-side
   * first (link needs a UUID site, create needs a name, ignore needs a reason)
   * so a provider GID can never be sent into a UUID field. On a 409 version
   * conflict the backend returns the current mapping — we surface it and
   * refresh so the operator retries against fresh state (no silent overwrite).
   */
  async function saveMappings(companyId, integrationId, changes) {
    for (const change of changes) {
      const { ok, errors } = validateMappingChange(change);
      if (!ok) {
        const e = { data: { message: `Invalid mapping: ${errors.join("; ")}` } };
        toast.error(e.data.message);
        throw e;
      }
    }
    commandLoading.value = "map";
    try {
      const res = await shopifyService.upsertMappings(companyId, integrationId, {
        // ai-back validates this route/body pair independently. Include the current
        // company identity rather than relying on a frontend-only path convention.
        company_id: companyId,
        mappings: changes,
      });
      if (Array.isArray(res.data)) commit(locations, res.data);
      // A mapping response owns row state, but the integration summary owns derived
      // counts/readiness. Re-read it so an open workspace can immediately start a
      // correctly acknowledged partial sync without a browser reload.
      await Promise.all([
        fetchIntegration(companyId, integrationId).catch(() => {}),
        fetchLocations(companyId, integrationId).catch(() => {}),
      ]);
      toast.success(res.message || "Location mappings saved.");
      return res;
    } catch (err) {
      // 409 mapping_version_conflict / duplicate-site-in-integration.
      if (err?.status === 409 || err?.data?.error_code) {
        toast.error(
          err?.data?.message || "Mapping was changed by someone else — refresh and retry."
        );
        // Refresh locations so the stale row is replaced.
        await fetchLocations(companyId, integrationId).catch(() => {});
        throw err;
      }
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  /** Locations that must be resolved before a full sync (empty => ready). */
  function pendingActiveLocations() {
    return unresolvedActiveLocations(locations.value);
  }

  /** Same as pendingActiveLocations() but returns the raw location records. */
  function pendingLocationRecords() {
    return pendingActiveLocationRecords(locations.value);
  }

  // ---- Scope + preview -----------------------------------------------------

  async function updateProjectionSettings(companyId, integrationId, settings) {
    commandLoading.value = "projection";
    try {
      const res = await shopifyService.updateProjectionSettings(companyId, integrationId, {
        ...settings,
        company_id: companyId,
      });
      if (res?.data?.id) commit(current, res.data);
      toast.success(res.message || "Scope updated.");
      return res;
    } catch (err) {
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  async function fetchPreview(companyId, integrationId, scope = {}) {
    commandLoading.value = "preview";
    try {
      const res = await shopifyService.preview(companyId, integrationId, {
        ...scope,
        company_id: companyId,
      });
      return stripCredentials(res.data ?? res);
    } catch (err) {
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  // ---- Sync / durable runs -------------------------------------------------

  async function startSync(companyId, integrationId, { mappedOnly = false, acknowledgedOmitted = [] } = {}) {
    commandLoading.value = "sync";
    try {
      const res = await shopifyService.startSync(companyId, integrationId, {
        company_id: companyId,
        mapped_only: mappedOnly,
        acknowledged_omitted: acknowledgedOmitted,
      });
      if (res?.data?.id) commit(activeRun, res.data);
      toast.success(res.message || "Sync started.");
      return res;
    } catch (err) {
      // 409 mapping_incomplete / already_running.
      if (err?.status === 409) {
        toast.error(err?.data?.message || "Cannot start sync yet.");
        throw err;
      }
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  async function reconcile(companyId, integrationId) {
    commandLoading.value = "reconcile";
    try {
      const res = await shopifyService.reconcile(companyId, integrationId, { company_id: companyId });
      if (res?.data?.id) commit(activeRun, res.data);
      toast.success(res.message || "Reconcile started.");
      return res;
    } catch (err) {
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  async function fetchRuns(companyId, integrationId, params = {}) {
    loading.value = true;
    try {
      const res = await shopifyService.getRuns(companyId, integrationId, params);
      commit(runs, res.data || []);
      return runs.value;
    } catch (err) {
      return _err(err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Read a single durable run by id. This is the refresh-during-sync resume
   * path: on page reload the view calls fetchRun(scopedRunId) and the phase
   * bar continues from the persisted phase — nothing is lost.
   */
  async function fetchRun(companyId, integrationId, runId) {
    try {
      const res = await shopifyService.getRun(companyId, integrationId, runId);
      commit(activeRun, res.data);
      return activeRun.value;
    } catch (err) {
      return _err(err);
    }
  }

  async function retryRun(companyId, integrationId, runId) {
    commandLoading.value = "retry";
    try {
      const res = await shopifyService.retryRun(companyId, integrationId, runId, { company_id: companyId });
      if (res?.data?.id) commit(activeRun, res.data);
      toast.success(res.message || "Retry started.");
      return res;
    } catch (err) {
      // 409 not_retryable.
      if (err?.status === 409) {
        toast.error(err?.data?.message || "This run cannot be retried.");
        throw err;
      }
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  // ---- Operate commands ----------------------------------------------------

  async function pause(companyId, integrationId) {
    commandLoading.value = "pause";
    try {
      const res = await shopifyService.pause(companyId, integrationId, { company_id: companyId });
      if (res?.data?.id) commit(current, res.data);
      toast.success(res.message || "Integration paused.");
      return res;
    } catch (err) {
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  async function disconnect(companyId, integrationId) {
    commandLoading.value = "disconnect";
    try {
      // confirm:true is required by the contract — never an unconfirmed destroy.
      const res = await shopifyService.disconnect(companyId, integrationId, { company_id: companyId, confirm: true });
      if (res?.data?.id) commit(current, res.data);
      toast.success(res.message || "Integration disconnected.");
      return res;
    } catch (err) {
      return _err(err);
    } finally {
      commandLoading.value = "";
    }
  }

  async function reauthorize(companyId, integrationId, rawDomain, returnUrl) {
    const shop = rawDomain ? canonicalShopDomain(rawDomain) : undefined;
    connecting.value = true;
    try {
      // Reauthorize reuses the install endpoint for a fresh signed URL (DTO §4). The
      // frozen field is `shop`; the integration is re-found by (company, shop) on the
      // server, so integrationId is not needed on the wire. Without `return_url` the
      // signed OAuth state carries none, and the callback falls back to a raw JSON
      // response instead of bouncing the browser back into the dashboard.
      const res = await shopifyService.startReauthorize(companyId, integrationId, {
        ...(shop ? { shop } : {}),
        ...(returnUrl ? { return_url: returnUrl } : {}),
      });
      return res; // { install_url, state } — no token
    } catch (err) {
      return _err(err);
    } finally {
      connecting.value = false;
    }
  }

  function reset() {
    current.value = null;
    locations.value = [];
    runs.value = [];
    activeRun.value = null;
  }

  return {
    // state
    loading,
    connecting,
    commandLoading,
    integrations,
    current,
    locations,
    runs,
    activeRun,
    // connect
    requestInstallUrl,
    // reads
    fetchIntegrations,
    fetchIntegration,
    fetchLocations,
    discoverLocations,
    // mapping
    saveMappings,
    pendingActiveLocations,
    pendingLocationRecords,
    // scope + preview
    updateProjectionSettings,
    fetchPreview,
    // sync / runs
    startSync,
    reconcile,
    fetchRuns,
    fetchRun,
    retryRun,
    // operate
    pause,
    disconnect,
    reauthorize,
    reset,
  };
});
